// utils/setupSocketListeners.js
import {
  addSystemMessage,
  setChatSession,
  updateMessageById,
  replaceRecents,
  addUserMessage,
  addToMetacontentByMessageId,
  addColumnToMsgContent,
  addColumnToMetacontent,
  addToFieldUpdates,
  updateInlineRecordInArray,
} from '../redux/slices/sessionSlice.js';

import {
  setGenerating,         // ⬅ plain action, no timeout helper
  setGeneratingText,
  setLayoutGenerating,
} from '../redux/slices/generatingSlice.js';
import { setConnected } from '../redux/slices/websocketSlice.js';
import { setChatObjects } from '../redux/slices/chatObjectsSlice.js';
import { setUser, updateCredits } from '../redux/slices/userSlice.js';
import { setOrganization } from '../redux/slices/organizationSlice.js';
import { setAllInstructions } from '../redux/slices/instructionsSlice.js';
import { startEditing, startEditingBatch } from '../redux/slices/isEditingSlice.js';
import { updateDraftField } from '../redux/slices/draftRecordSlice.js';
import { flashFields } from '../redux/slices/flashFieldsSlice.js';
import { setValidationError } from '../redux/slices/validationSlice.js';
import { setChatUserObjects, updateFieldDetailByName } from '../redux/slices/chatUserObjects.js';
import { addTrail, updateTrail, addTrailStep, updateUiLayout } from '../redux/slices/sessionSlice.js';
import {
  setStartingUp,
  setReportButtonSelection,
  setTooManyRecordsOpen,
  setAiBatchUpdateData,
  setRunningAiFilter,
  setRunningAiAutofill,
  setReportCardProgressBar,
  setColumnAdded,
  setColumnOrder,          // NEW – keeps ui.columnOrders in sync
} from '../redux/slices/uiSlice.js';
import { addNotification } from '../redux/slices/notificationSlice.js';
import { applyFileUploadPatches } from '../redux/slices/sessionSlice.js';



function setupSocketListeners(socket, store) {


  socket.on('connect', () => {
    console.log('Connected to WebSocket server:', socket.id);
    store.dispatch(setConnected(true));
  });

  socket.on('disconnect', () => {
    console.log('Disconnected from WebSocket server');
    store.dispatch(setConnected(false));
  });

  socket.on('error', (error) => {
    console.error('WebSocket error:', error);
    store.dispatch(setConnected(false));
  });

  socket.on('addUserMessage', (payload) => {
    store.dispatch(addUserMessage(payload));
  });

  socket.on('addSystemMessage', (payload) => {
    store.dispatch(addSystemMessage(payload));
    console.log('add system message hit', payload);
    if (payload.type === 'text') return;
  });

  socket.on('sessionData', (session) => {
    store.dispatch(setChatSession(session));
    store.dispatch(setStartingUp(false));
  });

  socket.on('updateMessage', ({ messageId, updates }) => {
    store.dispatch(updateMessageById({ messageId, updates }));
  });

  socket.on('replaceRecents', (recents) => {
    store.dispatch(replaceRecents(recents));
  });

  socket.on('updateChatObjects', (chatObjects) => {
    console.log('updating chat objects:', chatObjects);
    store.dispatch(setChatObjects(chatObjects));
  });

  socket.on('updateChatUserObjects', (chatUserObjects) => {
    console.log('updating chat user objects:', chatUserObjects);
    store.dispatch(setChatUserObjects(chatUserObjects));
  });

  socket.on('updateInstructions', (instructions) => {
    try {
      console.log('updateInstructions received:', Array.isArray(instructions) ? instructions.length : 0, 'docs');
      store.dispatch(setAllInstructions(Array.isArray(instructions) ? instructions : []));
    } catch (e) {
      console.error('Failed to process updateInstructions:', e);
      store.dispatch(setAllInstructions([]));
    }
  });

  socket.on('changeGenerating', (isGenerating) => {
    store.dispatch(setGenerating(isGenerating));
  });

  socket.on('changeGeneratingText', (newText) => {
    store.dispatch(setGeneratingText(newText));
  });

  socket.on('setLayoutGenerating', (flag) => {       
    store.dispatch(setLayoutGenerating(!!flag));
  });

  socket.on('updateUser', (data) => {
    store.dispatch(setUser(data));
  });

  socket.on('updateOrganization', (data) => {
    store.dispatch(setOrganization(data));
  });

  socket.on('startEditing', ({ recordId, recordType, messageId, type }) => {
    store.dispatch(startEditing({ recordId, recordType, messageId, type }));
  });

  socket.on('draftUpdateFields', ({ recordId, updates, messageId }) => {
    console.log('draft update fields hit', updates);
    updates.forEach(({ fieldName, newValue, newDisplayValue }) => {
      store.dispatch(
        updateDraftField({
          recordId,
          messageId,
          fieldName,
          newValue,
          newDisplayValue,
        })
      );
    });
  });

  socket.on('addValidationErrors', ({ recordId, messageId, validations }) => {
    validations.forEach(({ fieldName, errorType }) => {
      store.dispatch(setValidationError({ recordId, messageId, fieldName, errorType }));
    });
  });

  socket.on('flashUpdatedFields', ({ recordId, fields, messageId }) => {
    store.dispatch(flashFields({ recordId, fields, messageId }));
  });

  socket.on('addToFieldUpdates', (update) => {
    store.dispatch(addToFieldUpdates(update));
  });

  socket.on('batchAddToFieldUpdates', (entries) => {
    entries.forEach((e) => store.dispatch(addToFieldUpdates(e)));
  });

  socket.on('updateCredits', (payload) => {
    console.log('updateCredits payload', payload);
    store.dispatch(updateCredits(payload));
  });

  socket.on('enrichedFieldSuccess', (payload) => {
    const { fieldName, fieldLabel, fieldValue, title, snippet, url } = payload;
    store.dispatch(
      addNotification({
        type: 'webEnrich',
        fieldName,
        fieldLabel,
        fieldValue,
        title,
        snippet,
        sourceUrl: url,
      })
    );
  });

  socket.on('reportCreatedinSalesforce', (payload) => {
    const { title, snippet, url } = payload;
    store.dispatch(
      addNotification({
        type: 'report',
        title,
        snippet,
        sourceUrl: url,
      })
    );
  });

  socket.on('reportCreationFailed', ({ message }) => {
    store.dispatch(
      addNotification({
        type: 'error',                      // uses your existing error icon
        title: 'Report creation failed',    // headline for the toast
        snippet: message                    // the actual error text
      })
    );
  });

  socket.on('updateChatUserObjectFieldDetail', (payload) => {
    console.log('updating chat user object payload', payload);
    store.dispatch(updateFieldDetailByName(payload));
  });

  socket.on('startEditingBatch', ({ messageId, type, records }) => {
    (records || []).forEach(({ recordId, recordType }) => {
      store.dispatch(startEditing({ recordId, recordType, messageId, type }));
    });
  });

  socket.on('draftUpdateBatch', ({ messageId, updates }) => {
    (updates || []).forEach(({ recordId, fields }) => {
      fields.forEach(({ fieldName, newValue, displayValue }) => {
        if (!newValue || !newValue.trim()) return;
        store.dispatch(
          updateDraftField({
            recordId,
            messageId,
            fieldName,
            newValue,
            displayName: displayValue,
          })
        );
      });
    });
    store.dispatch(setReportButtonSelection({ messageId, selection: 'drafted' }));
  });

  socket.on('updateInlineMessage', ({ messageId, recordId, updatedRecord }) => {
    store.dispatch(updateInlineRecordInArray({ messageId, recordId, updatedRecord }));
  });

  socket.on('tooMuchAiForBatchUpdate', ({ messageId, batchUpdate }) => {
    console.log('Received tooMuchAiForBatchUpdate', { messageId });
    store.dispatch(setAiBatchUpdateData({ messageId, batchUpdate }));
    store.dispatch(setTooManyRecordsOpen(true));
  });

  socket.on('startRunningAiFilter', (payload) => {
    store.dispatch(
      setRunningAiFilter({ isRunning: true, messageId: payload.messageId, progress: 0, status: 'running' })
    );
  });

  socket.on('updateAiFilterProgress', (payload) => {
    const status = payload.status === 'complete' ? 'complete' : 'running';
    const progress = status === 'complete' ? 100 : payload.progress;
    store.dispatch(
      setRunningAiFilter({ isRunning: status === 'running', messageId: payload.messageId, progress, status })
    );
  });

  socket.on('startRunningAiAutofill', (payload) => {
    store.dispatch(
      setRunningAiAutofill({ isRunning: true, messageId: payload.messageId, progress: 0, status: 'running' })
    );
  });

  socket.on('updateAiAutofillProgress', (payload) => {
    const status = payload.status === 'complete' ? 'complete' : 'running';
    const progress = status === 'complete' ? 100 : payload.progress;
    store.dispatch(
      setRunningAiAutofill({ isRunning: status === 'running', messageId: payload.messageId, progress, status })
    );
  });

  socket.on('startReportCardProgress', (payload) => {
    console.log('[socket] startReportCardProgress received:', payload);
    store.dispatch(
      setReportCardProgressBar({
        isRunning: true,
        messageId: payload.messageId,
        progress: 0,
        status: 'running',
        progressExplainer: payload.progressExplainer ?? '',
      })
    );
  });

  socket.on('updateReportCardProgress', (payload) => {
    console.log('[socket] updateReportCardProgress received:', payload);
    const status = payload.status === 'complete' ? 'complete' : 'running';
    const progress = status === 'complete' ? 100 : payload.progress;
    store.dispatch(
      setReportCardProgressBar({
        isRunning: status === 'running',
        messageId: payload.messageId,
        progress,
        status,
        progressExplainer: payload.progressExplainer ?? '',
      })
    );
  });

  socket.on('addToMetacontent', ({ messageId, key, value }) => {
    store.dispatch(addToMetacontentByMessageId({ messageId, key, value }));
  });



  socket.on('addColumnToMsgContent', ({ messageId, fieldName, updates }) => {
    // 1) merge the new field values into each row
    store.dispatch(addColumnToMsgContent({ messageId, fieldName, updates }));

    // 2) append to metacontent.columnOrder inside the message
    store.dispatch(addColumnToMetacontent({ messageId, fieldName }));

    // 3) keep ui.columnOrders in sync *if* the user has already customised order
    const state = store.getState();
    const currentOrder = state.ui?.columnOrders?.[messageId];
    if (Array.isArray(currentOrder) && !currentOrder.some((c) => c.field === fieldName)) {
      store.dispatch(
        setColumnOrder({
          messageId,
          columnOrder: [...currentOrder, { field: fieldName }], // NEW append
        })
      );
    }

    // 4) flag the UI so ReportTable scrolls to the far right once
    store.dispatch(setColumnAdded({ messageId, added: true }));
  });

  socket.on(
    'addCalculatedFieldsToMetacontent',
    ({ messageId, calculatedField }) => {
      // uses the same reducer to init and append to metacontent.calculatedFields
      store.dispatch(
        addToMetacontentByMessageId({
          messageId,
          key: 'calculatedFields',
          value: calculatedField,
        })
      );
    }
  );

    socket.on('addTrail', (payload) => {                     // ★ NEW
    // backend emits full trail object
    store.dispatch(addTrail(payload));
  });

  socket.on('updateTrail', (payload) => {                  // ★ NEW
    console.log('update trail hit', payload);
    store.dispatch(updateTrail(payload));
  });

  socket.on('addTrailStep', (payload) => {        // 🆕 listener
    // payload = { trailId, trailStep }  OR  { trailId, trailSteps: [ … ] }
    store.dispatch(addTrailStep(payload));
  });

  socket.on('newUiLayout', (layout) => {
    store.dispatch(updateUiLayout(layout));
  });

  socket.on('applyFileUploadPatches', ({ updates }) => { // NEW
  // updates = [{ fileUploadId, status, userMessageId }]
  if (!Array.isArray(updates) || !updates.length) return;
  store.dispatch(applyFileUploadPatches({ updates }));
});

}

export default setupSocketListeners;